import React, { useState, useMemo, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { FiHeart, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useServerConfig } from '../../hooks/useServerConfig';
import styles from './DonationForm.module.css';
import { useDonationForm } from './useDonationForm';
import DonationAmountSection from './donationForm/DonationAmountSection';
import DonorInfoSection from './donationForm/DonorInfoSection';
import DonationPaymentSection from './donationForm/DonationPaymentSection';
import DonationTypeSelector from './donationForm/DonationTypeSelector';
import stripeAppearance from './donationForm/stripeAppearance';


function DonationFormContent(props) {
  const { onClientSecretChange, clientSecretFromParent, preservedOneTimeAmount, setPreservedOneTimeAmount, setPreservedDonationType } = props;
  const {
    clientSecret,
    creatingPaymentIntent,
    donationFor,
    donationType,
    elements,
    email,
    error,
    firstName,
    formatCurrency,
    handleProceedToPayment,
    handleSubmit,
    hasExpressCheckout,
    isForever,
    lastName,
    loading,
    monthlyAmount,
    navigate,
    numberOfPayments,
    oneTimeAmount,
    paymentIntentCreatedRef,
    phone,
    preservedIsForever,
    preservedMonthlyAmount,
    preservedNumberOfPayments,
    setClientSecret,
    setDonationFor,
    setDonationType,
    setEmail,
    setError,
    setFirstName,
    setHasExpressCheckout,
    setIsForever,
    setLastName,
    setMonthlyAmount,
    setNumberOfPayments,
    setOneTimeAmount,
    setPaymentIntentId,
    setPhone,
    setPreservedIsForever,
    setPreservedMonthlyAmount,
    setPreservedNumberOfPayments,
    setShowPaymentElement,
    setShowPaymentForm,
    setSubscriptionData,
    showPaymentElement,
    showPaymentForm,
    stripe,
    success,
    totalAmount,
    user,
  } = useDonationForm(props);

  return (
    <div className={styles.donationCard}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <FiHeart /> Make a Donation
        </h1>
        <p className={styles.subtitle}>Support our mission with a one-time or recurring donation</p>
      </div>

      {error && <div className={styles.errorMessage}><FiAlertCircle className={styles.errorIcon} />{error}</div>}
      {success && (
        <div className={styles.successMessage}>
          <FiCheck /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <DonationTypeSelector
          donationType={donationType}
          onClientSecretChange={onClientSecretChange}
          paymentIntentCreatedRef={paymentIntentCreatedRef}
          setClientSecret={setClientSecret}
          setDonationType={setDonationType}
          setError={setError}
          setIsForever={setIsForever}
          setMonthlyAmount={setMonthlyAmount}
          setNumberOfPayments={setNumberOfPayments}
          setOneTimeAmount={setOneTimeAmount}
          setPaymentIntentId={setPaymentIntentId}
          setPreservedDonationType={setPreservedDonationType}
          setPreservedIsForever={setPreservedIsForever}
          setPreservedMonthlyAmount={setPreservedMonthlyAmount}
          setPreservedNumberOfPayments={setPreservedNumberOfPayments}
          setPreservedOneTimeAmount={setPreservedOneTimeAmount}
          setShowPaymentElement={setShowPaymentElement}
          setShowPaymentForm={setShowPaymentForm}
          setSubscriptionData={setSubscriptionData}
        />

        <DonationAmountSection
          onClientSecretChange={onClientSecretChange}
          preservedOneTimeAmount={preservedOneTimeAmount}
          setPreservedOneTimeAmount={setPreservedOneTimeAmount}
          clientSecret={clientSecret}
          donationType={donationType}
          isForever={isForever}
          monthlyAmount={monthlyAmount}
          numberOfPayments={numberOfPayments}
          oneTimeAmount={oneTimeAmount}
          paymentIntentCreatedRef={paymentIntentCreatedRef}
          preservedIsForever={preservedIsForever}
          preservedMonthlyAmount={preservedMonthlyAmount}
          preservedNumberOfPayments={preservedNumberOfPayments}
          setClientSecret={setClientSecret}
          setIsForever={setIsForever}
          setMonthlyAmount={setMonthlyAmount}
          setNumberOfPayments={setNumberOfPayments}
          setOneTimeAmount={setOneTimeAmount}
          setPaymentIntentId={setPaymentIntentId}
          setPreservedMonthlyAmount={setPreservedMonthlyAmount}
          setShowPaymentElement={setShowPaymentElement}
          setShowPaymentForm={setShowPaymentForm}
          showPaymentElement={showPaymentElement}
          showPaymentForm={showPaymentForm}
          user={user}
        />

      {!user && (
        <DonorInfoSection
          donationFor={donationFor}
          email={email}
          firstName={firstName}
          lastName={lastName}
          phone={phone}
          setDonationFor={setDonationFor}
          setEmail={setEmail}
          setFirstName={setFirstName}
          setLastName={setLastName}
          setPhone={setPhone}
        />
      )}

        {/* Show PaymentElement for:
            1. Old flow: when we have clientSecret (one-time or forever subscriptions)
            2. New flow: when monthly donation is selected with valid amount/payments for PaymentMethod creation
        */}
        <DonationPaymentSection
          clientSecretFromParent={clientSecretFromParent}
          clientSecret={clientSecret}
          creatingPaymentIntent={creatingPaymentIntent}
          donationType={donationType}
          elements={elements}
          email={email}
          firstName={firstName}
          formatCurrency={formatCurrency}
          handleProceedToPayment={handleProceedToPayment}
          hasExpressCheckout={hasExpressCheckout}
          isForever={isForever}
          lastName={lastName}
          loading={loading}
          monthlyAmount={monthlyAmount}
          navigate={navigate}
          numberOfPayments={numberOfPayments}
          oneTimeAmount={oneTimeAmount}
          preservedIsForever={preservedIsForever}
          preservedMonthlyAmount={preservedMonthlyAmount}
          preservedNumberOfPayments={preservedNumberOfPayments}
          preservedOneTimeAmount={preservedOneTimeAmount}
          setError={setError}
          setHasExpressCheckout={setHasExpressCheckout}
          showPaymentElement={showPaymentElement}
          showPaymentForm={showPaymentForm}
          stripe={stripe}
          totalAmount={totalAmount}
          user={user}
        />
      </form>
    </div>
  );
}

function DonationForm() {
  const { config, loading, error } = useServerConfig();
  const [clientSecret, setClientSecret] = useState(null);
  const [preservedOneTimeAmount, setPreservedOneTimeAmount] = useState(null); // Preserve amount after payment intent creation
  const [preservedDonationType, setPreservedDonationType] = useState(null); // Preserve donation type to prevent reset on remount
  // Preserve monthly donation values in parent to survive remounts
  const [preservedMonthlyAmount, setPreservedMonthlyAmount] = useState(null);
  const [preservedNumberOfPayments, setPreservedNumberOfPayments] = useState(null);
  const [preservedIsForever, setPreservedIsForever] = useState(false);
  // Preserve subscription data to identify subscription payments after remount
  const [preservedSubscriptionData, setPreservedSubscriptionData] = useState(null);
  const [shouldLoadStripe, setShouldLoadStripe] = useState(false);

  const handleClientSecretChange = useCallback((newClientSecret) => {
    setClientSecret(newClientSecret);
    setShouldLoadStripe(!!newClientSecret);
  }, []);
  
  
  // Use useMemo to ensure Stripe promise is only created once
  // IMPORTANT: Only depend on the stripe key value, not the entire config object
  // This prevents Elements from complaining about stripe prop changes
  const stripeKey = useMemo(() => {
    // Try server config first
    let key = config?.stripePublishableKey;
    
    // Fallback to REACT_APP_STRIPE_PUBLISHABLE_KEY if server config doesn't have it
    if (!key && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    }
    
    return key;
  }, [config?.stripePublishableKey]);
  
  const stripePromise = useMemo(() => {
    if (stripeKey && shouldLoadStripe) {
      return loadStripe(stripeKey);
    }
    return null;
  }, [stripeKey, shouldLoadStripe]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.donationCard}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment configuration...
          </div>
        </div>
      </div>
    );
  }

  if (error || !config || !config.stripePublishableKey) {
    return (
      <div className={styles.container}>
        <div className={styles.donationCard}>
          <div className={styles.errorMessage}>
            Stripe is not properly configured. Please check your server configuration.
            {error && <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Error: {error}</div>}
          </div>
        </div>
      </div>
    );
  }

  const appearance = stripeAppearance;

  // Determine Elements options - must have either clientSecret or mode
  // For one-time donations, we need to wait for clientSecret before initializing Elements
  // This prevents the "setup mode cannot be confirmed with Payment Intent" error
  
  const elementsOptions = { clientSecret, appearance, paymentMethodCreation: 'manual' };
  
  // Force remount Elements when clientSecret changes from null to a value or vice versa
  const elementsKey = clientSecret ? `donation-${clientSecret.substring(0, 20)}` : 'donation-form-setup';
  
  return (
    <div className={styles.container}>
      {stripePromise ? (
        <Elements
          stripe={stripePromise}
          options={elementsOptions}
          key={elementsKey}
        >
          <DonationFormContent
            onClientSecretChange={handleClientSecretChange}
            clientSecretFromParent={clientSecret}
            preservedOneTimeAmount={preservedOneTimeAmount}
            setPreservedOneTimeAmount={setPreservedOneTimeAmount}
            preservedDonationType={preservedDonationType}
            setPreservedDonationType={setPreservedDonationType}
            preservedMonthlyAmount={preservedMonthlyAmount}
            setPreservedMonthlyAmount={setPreservedMonthlyAmount}
            preservedNumberOfPayments={preservedNumberOfPayments}
            setPreservedNumberOfPayments={setPreservedNumberOfPayments}
            preservedIsForever={preservedIsForever}
            setPreservedIsForever={setPreservedIsForever}
            preservedSubscriptionData={preservedSubscriptionData}
            setPreservedSubscriptionData={setPreservedSubscriptionData}
          />
        </Elements>
      ) : (
        <Elements stripe={null} options={{ mode: 'setup', currency: 'usd', appearance }} key="donation-form-deferred">
          <DonationFormContent
            onClientSecretChange={handleClientSecretChange}
            clientSecretFromParent={clientSecret}
            preservedOneTimeAmount={preservedOneTimeAmount}
            setPreservedOneTimeAmount={setPreservedOneTimeAmount}
            preservedDonationType={preservedDonationType}
            setPreservedDonationType={setPreservedDonationType}
            preservedMonthlyAmount={preservedMonthlyAmount}
            setPreservedMonthlyAmount={setPreservedMonthlyAmount}
            preservedNumberOfPayments={preservedNumberOfPayments}
            setPreservedNumberOfPayments={setPreservedNumberOfPayments}
            preservedIsForever={preservedIsForever}
            setPreservedIsForever={setPreservedIsForever}
            preservedSubscriptionData={preservedSubscriptionData}
            setPreservedSubscriptionData={setPreservedSubscriptionData}
          />
        </Elements>
      )}
    </div>
  );
}

export default DonationForm;
